import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Flower } from "lucide-react";

const testimonials = [
  {
    quote:
      "Mercy helped me build a routine I actually stick to. My energy and focus have never been better.",
    author: "Aisha K., Product Designer",
  },
  {
    quote:
      "The yoga-nutrition combo was a game changer. I feel lighter, calmer, and more in control.",
    author: "Daniel M., Freelance Developer",
  },
  {
    quote:
      "I stopped chasing quick fixes. Mercy's approach is sustainable and compassionate.",
    author: "Priya S., Photographer",
  },
];

// Rotating line of services inside hero heading (no layout shift)
const RotatingServicesLine = React.memo(function RotatingServicesLine() {
  // Curated short phrases for clarity & aesthetics
  const phrases = useMemo(
    () => [
      "Clinical Nutrition",
      "Personalized Meal Planning",
      "Yoga + Nutrition Workshops",
      "Sustainable Eating",
      "Nutrition + Yoga Packages",
      "Ongoing Support",
      "Retreat Menu Planning",
      "Habit Change Coaching",
    ],
    []
  );

  const [index, setIndex] = useState(0);
  const intervalMs = 2600;

  useEffect(() => {
    const id = setInterval(
      () => setIndex((i) => (i + 1) % phrases.length),
      intervalMs
    );
    return () => clearInterval(id);
  }, [phrases.length]);

  // Reserve height using a hidden static placeholder (avoids CLS)
  const tallest = useMemo(
    () => phrases.reduce((a, b) => (a.length > b.length ? a : b), ""),
    [phrases]
  );

  return (
    <span
      className="relative block text-accent selection:bg-secondary selection:text-secondary-foreground"
      aria-label="Key services offered"
    >
      {/* Hidden placeholder reserves height; allow wrapping on very small screens */}
      <span className="block opacity-0 pointer-events-none select-none font-serif font-bold tracking-tight whitespace-normal max-w-full wrap-break-word">
        {tallest}
      </span>
      <span className="absolute inset-0" style={{ overflow: "visible" }}>
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={index}
            initial={{ y: "100%", opacity: 0 }}
            animate={{ y: "0%", opacity: 1 }}
            exit={{ y: "-100%", opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="block font-serif font-bold tracking-tight whitespace-normal max-w-full wrap-break-word will-change-transform"
          >
            {phrases[index]}
          </motion.span>
        </AnimatePresence>
      </span>
    </span>
  );
});

const BackgroundBlob = React.memo(() => (
  <div
    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] md:w-[900px] md:h-[900px] bg-white/40 rounded-full blur-3xl -z-10 pointer-events-none animate-blob will-change-transform"
    style={{
      background:
        "radial-gradient(circle, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0) 70%)",
      backfaceVisibility: "hidden",
    }}
  />
));

const Hero = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section
      id="home"
      className="relative pt-24 pb-20 md:pt-32 md:pb-28 overflow-hidden"
    >
      {/* Organic Background Blob */}
      <BackgroundBlob />

      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block py-0.5 px-2.5 md:py-1 md:px-3 rounded-full bg-white/40 border border-white/30 backdrop-blur-md text-muted-foreground text-[9px] md:text-[10px] font-medium mb-4 md:mb-6 tracking-[0.15em] md:tracking-[0.2em] uppercase shadow-sm">
              Healing • Health • Happiness
            </span>
            <h1 className="font-serif text-3xl md:text-6xl lg:text-7xl font-bold text-foreground tracking-tight leading-tight md:leading-[1.1]">
              Find your balance
              <br />
              <span className="text-primary">Nourish your body</span> <br />
              Thrive every day
            </h1>
            <div className="text-3xl md:text-6xl lg:text-7xl mb-4 md:mb-8 leading-tight md:leading-[1.1] text-muted-foreground">
              <RotatingServicesLine />
            </div>
            <p className="text-lg md:text-xl text-muted-foreground max-w-xl mx-auto leading-relaxed text-balance mb-8 md:mb-12 font-light">
              Nurturing whole person wellness through evidence-based nutrition,
              mindful movement, and sustainable lifestyle practices.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
              <a
                href="https://cal.com/wellnessbymercy"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto px-8 py-4 bg-primary text-primary-foreground rounded-full font-medium hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
              >
                Book a meeting now
                <ArrowRight size={18} />
              </a>
              <a
                href="#services"
                className="w-full sm:w-auto px-8 py-4 bg-secondary text-secondary-foreground rounded-full font-medium hover:bg-secondary/80 transition-colors"
              >
                Explore our services
              </a>
            </div>
            <p className="text-xs text-muted-foreground font-medium tracking-widest uppercase mb-16">
              Trusted by 500+ Clients • Over 5 Years of Professional Experience
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="border-t border-border pt-10"
          >
            <p className="text-sm text-muted-foreground uppercase tracking-wider font-medium mb-6">
              Featured In
            </p>
            <div className="flex flex-wrap justify-center gap-8 md:gap-16 opacity-50 grayscale">
              <div className="text-xl font-serif font-bold">
                Mid-Day Newspaper
              </div>
              <div className="text-xl font-serif font-bold">
                Nutrition Insight Magazine
              </div>
            </div>
          </motion.div>

          <div className="mt-20 max-w-2xl mx-auto p-8 rounded-3xl bg-white/30 backdrop-blur-md border border-white/20 shadow-sm hover:shadow-md transition-all duration-500">
            <div className="relative h-32 md:h-24">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentTestimonial}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.4 }}
                  className="absolute inset-0 flex flex-col items-center justify-center"
                >
                  <p className="text-lg md:text-xl text-foreground font-medium italic mb-2">
                    "{testimonials[currentTestimonial]?.quote}"
                  </p>
                  <p className="text-sm text-muted-foreground">
                    — {testimonials[currentTestimonial]?.author}
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>
            <div className="flex justify-center gap-2 mt-4">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    index === currentTestimonial ? "bg-foreground" : "bg-muted"
                  }`}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
